/* Gut Ritual quantity-tier subscription selector (plan 001).
 *
 * The tiles are rendered server-side (snippets/gut-tier-selector.liquid). This
 * script only attaches interaction: on tier click it writes quantity AND
 * selling_plan together, atomically, so qty N always carries the matching
 * every-N-months Skio plan (the lockstep that replaces the variant/quantity
 * model). The "BUY ONCE" button buys a single bottle one-time (no plan).
 *
 * Each tile carries data-months (= quantity = the tier), data-plan (= the Skio
 * selling-plan id) and data-total (= the server-formatted line total). The
 * hidden inputs are pre-filled server-side with the preselected tier, so the
 * form is valid before this runs.
 *
 * It keeps three things in lockstep on every tier change:
 *   1. the main product form's quantity + selling_plan (form= association),
 *   2. the primary ATC button label (appends " • {tier total}"),
 *   3. the sticky add-to-cart form, which renders its OWN <form> without our
 *      inputs — without this it would add a one-time single bottle.
 */
(() => {
  const ROOT_SELECTOR = '[data-gut-tier-selector]';

  function deliveryText(months) {
    return months === 1
      ? 'Refilled every month'
      : `Refilled every ${months} months`;
  }

  // Parse the server-emitted full backend ladder "month:planId,month:planId,..."
  // (data-full-ladder) into [{qty, planId}]. Empty/absent -> [] (O3 falls back to
  // the visible cards).
  function parseFullLadder(str) {
    return (str || '')
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean)
      .map((pair) => {
        const idx = pair.indexOf(':');
        return { qty: parseInt(pair.slice(0, idx), 10), planId: String(pair.slice(idx + 1)) };
      })
      .filter((e) => e.qty > 0 && e.planId);
  }

  // Ensure a hidden input of the given name exists inside `form`; return it.
  function ensureHidden(form, name) {
    let el = form.querySelector(`[name="${name}"]`);
    if (!el) {
      el = document.createElement('input');
      el.type = 'hidden';
      el.name = name;
      form.appendChild(el);
    }
    return el;
  }

  function init(root) {
    if (root.dataset.gutTierInit) return;
    root.dataset.gutTierInit = '1';

    const tiles = [...root.querySelectorAll('.gut-tier-tile')];
    const qtyInput = root.querySelector('[data-gut-tier-qty]');
    const planInput = root.querySelector('[data-gut-tier-plan]');
    if (!tiles.length || !qtyInput || !planInput) return;

    const formId = root.dataset.formId;
    const mainForm = formId ? document.getElementById(formId) : null;
    const primaryBtn = mainForm
      ? mainForm.querySelector('button[name="add"]')
      : document.querySelector('product-form:not(.sticky-atc) button[name="add"]');
    const primaryLabel = primaryBtn ? primaryBtn.querySelector('span') : null;
    // Capture the merchant's label once (strip any prior " • total").
    const baseLabel = primaryLabel ? primaryLabel.textContent.split('•')[0].trim() : '';

    const postEl = document.querySelector('[data-gut-tier-post]');
    const deliveryEl = postEl ? postEl.querySelector('[data-gut-tier-delivery]') : null;
    const freemonthEl = postEl ? postEl.querySelector('[data-gut-tier-freemonth]') : null;
    const freemonthTextEl = freemonthEl ? freemonthEl.querySelector('span') : null;
    const freeshipEl = postEl ? postEl.querySelector('[data-gut-tier-freeship]') : null;
    const onetimeBtn = postEl ? postEl.querySelector('[data-gut-tier-onetime]') : null;
    // BUY ONCE price is dynamic: it shows the one-time total for the SELECTED
    // quantity (qty N x full bottle price), not a single bottle (Fabio 2026-06-14).
    const onetimePriceEl = onetimeBtn
      ? onetimeBtn.querySelector('.gut-tier-post__onetime-price')
      : null;

    // Sticky ATC renders its own form without our inputs — mirror the tier into
    // it so the sticky button adds the selected subscription, not a single bottle.
    const stickyForm = document.querySelector('.sticky-atc form');

    // Defensive: the dormant info.product_is_sub scaffold also emits a
    // [name="selling_plan"].pp_sub_plan_value. Keep any stray copy in sync so a
    // mis-set metafield can't submit a conflicting plan. (Contract: that flag
    // stays OFF for quantity-tier offers — this is just insurance.)
    const strayPlans = () => [...document.querySelectorAll('.pp_sub_plan_value')];

    let selectedPlan = planInput.value;
    let selectedMonths = parseInt(qtyInput.value, 10) || 1;

    function setMainPlan(plan) {
      planInput.value = plan;
      strayPlans().forEach((el) => { el.value = plan; });
    }

    function syncSticky(months, plan) {
      if (!stickyForm) return;
      ensureHidden(stickyForm, 'selling_plan').value = plan;
      ensureHidden(stickyForm, 'quantity').value = String(months);
      // The sticky form renders without a variant id input — mirror it so a
      // sticky subscription add never posts id:null.
      const mid = mainForm && mainForm.querySelector('[name="id"]');
      if (mid) ensureHidden(stickyForm, 'id').value = mid.value;
    }

    function applyButtonTotal(total) {
      if (primaryLabel && total) {
        primaryLabel.textContent = baseLabel ? `${baseLabel} • ${total}` : total;
      }
    }

    // Free-gift variant ids for the currently selected tier. They are zeroed by
    // a 100% automatic discount in Shopify (or are $0 variants). Added alongside
    // the subscription line by the consolidation flow — the one-time "buy once"
    // path adds no gifts.
    function giftIdsForSelection() {
      const tile = tiles.find((t) => t.getAttribute('aria-checked') === 'true');
      if (!tile) return [];
      return (tile.dataset.giftVariants || '')
        .split(',')
        .map((s) => parseInt(s.trim(), 10))
        .filter((n) => n > 0);
    }

    function select(tile) {
      const months = parseInt(tile.dataset.months, 10);
      // Lockstep: quantity + selling plan written together, always.
      qtyInput.value = String(months);
      setMainPlan(tile.dataset.plan);
      selectedPlan = tile.dataset.plan;
      selectedMonths = months;
      tiles.forEach((t) => t.setAttribute('aria-checked', t === tile ? 'true' : 'false'));
      // Free-month line: per-tier copy (e.g. "1st month is FREE"); shown only on
      // tiers that carry it (the BOGO tier), hidden otherwise.
      if (freemonthEl) {
        const freeLine = tile.dataset.freeLine || '';
        freemonthEl.hidden = !freeLine;
        if (freemonthTextEl) freemonthTextEl.textContent = freeLine;
      }
      // Delivery/rebill line: per-tier copy if the tile sets one, else the generic
      // "Refilled every N months".
      if (deliveryEl) deliveryEl.textContent = tile.dataset.rebillLine || deliveryText(months);
      // Free Shipping point below the CTA mirrors the selected tier's benefits:
      // shown only on tiers whose card lists free shipping (O1 tiers 2 & 3, not 1).
      if (freeshipEl) freeshipEl.hidden = tile.dataset.freeship !== 'true';
      applyButtonTotal(tile.dataset.total);
      // BUY ONCE reflects the one-time total for the selected quantity.
      if (onetimePriceEl && tile.dataset.onetimeTotal) {
        onetimePriceEl.textContent = tile.dataset.onetimeTotal;
      }
      syncSticky(months, tile.dataset.plan);
    }

    tiles.forEach((tile) => tile.addEventListener('click', () => select(tile)));

    // --- Cart stacking (plan 003): one subscription line per offer. ----------
    // Intercept the form submit in the CAPTURE phase so we run BEFORE the theme's
    // <product-form> bubble-phase handler. For a subscription add we block
    // product-form and route through the generic consolidation module
    // (assets/sub-cart-stack.js): accumulate qty -> snap up to next tier -> cap ->
    // ONE line. A one-time add (plan blanked, e.g. the BUY ONCE button) is not
    // ours — we let it fall through to the normal product-form add.
    // Tiers the customer can SEE (1..3) carry the offer-specific plans (incl. O3
    // BOGO). The full backend ladder (1..7 N-month plans, plan 004) is emitted
    // server-side as data-full-ladder so a card-add can ACCUMULATE past the 3
    // visible cards up to 7 — e.g. add "1 bottle" then "3 bottle" => qty 4 / real
    // 4-month plan, climbing to 7, exactly like the cart "+" stepper. Empty for
    // O3 (no "N Month" plans) -> falls back to the 3 visible cards.
    const tileTiers = tiles.map((t) => ({ qty: parseInt(t.dataset.months, 10), planId: String(t.dataset.plan) }));
    const ladderTiers = parseFullLadder(root.dataset.fullLadder);
    const built =
      window.SubCartStack && window.SubCartStack.buildOfferTiers
        ? window.SubCartStack.buildOfferTiers(tileTiers, ladderTiers)
        : { tiers: tileTiers, planIds: tileTiers.map((e) => e.planId) };
    const tierList = built.tiers;
    const myPlanIdSet = new Set(built.planIds);
    let atcBusy = false;

    function setLoading(btn, on) {
      if (!btn) return;
      btn.classList.toggle('loading', on);
      if (on) btn.setAttribute('aria-disabled', 'true');
      else btn.removeAttribute('aria-disabled');
      const spinner = btn.querySelector('.loading__spinner');
      if (spinner) spinner.classList.toggle('hidden', !on);
    }

    // Read a form value via FormData so form-ASSOCIATED inputs are seen. The
    // selector's [name=quantity]/[name=selling_plan] live OUTSIDE the <form>
    // (linked by form=), so form.querySelector() would miss them — FormData
    // picks them up exactly as the theme's product-form does.
    function formVal(form, name) {
      try { return new FormData(form).get(name); } catch (e) { return null; }
    }

    function showAtcError(form) {
      const wrap = form.querySelector('.product-form__error-message-wrapper');
      const msg = wrap && wrap.querySelector('.product-form__error-message');
      if (wrap && msg) {
        msg.textContent = "Sorry — we couldn't update your cart. Please try again.";
        wrap.removeAttribute('hidden');
      }
    }

    async function runConsolidation(form, btn) {
      if (atcBusy || !window.SubCartStack) return;
      const selectedQty = parseInt(formVal(form, 'quantity'), 10) || 1;
      const variantId = formVal(form, 'id') || (mainForm ? formVal(mainForm, 'id') : null);
      if (!variantId) {
        console.error('[gut-tier] no variant id — aborting consolidation');
        return; // never call the cart API with id:null
      }
      atcBusy = true;
      setLoading(btn, true);
      const cartDrawer = document.querySelector('cart-drawer');
      // Pass section ids as an array (Shopify JSON cart API accepts an array;
      // the response keys sections by id for cartDrawer.renderContents).
      const sections = cartDrawer ? cartDrawer.getSectionsToRender().map((s) => s.id) : null;
      if (cartDrawer) cartDrawer.setActiveElement(btn || document.activeElement);
      try {
        const { addResponse } = await window.SubCartStack.consolidateSubscriptionAdd({
          planIds: [...myPlanIdSet],
          tiers: tierList,
          variantId: variantId,
          selectedQty: selectedQty,
          extraItems: giftIdsForSelection(),
          sectionsToRender: sections,
          sectionsUrl: window.location.pathname,
        });
        if (addResponse && cartDrawer) cartDrawer.renderContents(addResponse);
        else if (cartDrawer) cartDrawer.open(btn);
        else window.location = (window.routes && window.routes.cart_url) || '/cart';
      } catch (err) {
        // The module restores any removed line on failure (no data loss).
        console.error('[gut-tier] cart consolidation failed', err);
        showAtcError(form);
      } finally {
        setLoading(btn, false);
        atcBusy = false;
      }
    }

    function onFormSubmitCapture(evt) {
      const form = evt.currentTarget;
      const planVal = String(formVal(form, 'selling_plan') || '');
      // Only our subscription adds; one-time (blank/foreign plan) passes through
      // to the theme's product-form handler untouched.
      if (!planVal || !myPlanIdSet.has(planVal)) return;
      evt.preventDefault();
      evt.stopImmediatePropagation();
      runConsolidation(form, form.querySelector('[name="add"]'));
    }

    if (mainForm) mainForm.addEventListener('submit', onFormSubmitCapture, true);
    if (stickyForm) stickyForm.addEventListener('submit', onFormSubmitCapture, true);

    if (onetimeBtn) {
      let onetimeBusy = false;
      onetimeBtn.addEventListener('click', () => {
        // One-time = the SELECTED quantity, no plan (Fabio 2026-06-14: buying 3
        // bottles selected adds 3 one-time). qty stays at the tier; only the
        // selling plan is blanked for this submit, then restored so a later
        // subscribe click is unaffected. product-form reads FormData synchronously
        // on submit, so the plan only needs to be blank for that tick — restore
        // quickly (a long window let a fast tap submit a blank plan and miss the
        // sub). Guard against double-clicks (this button is not the one
        // product-form disables).
        if (onetimeBusy) return;
        onetimeBusy = true;
        qtyInput.value = String(selectedMonths);
        setMainPlan('');
        setTimeout(() => {
          setMainPlan(selectedPlan);
          onetimeBusy = false;
        }, 150);
      });
    }

    // Initialise everything from the server-preselected tile (no flash).
    const selected =
      tiles.find((t) => t.getAttribute('aria-checked') === 'true') || tiles[tiles.length - 1];
    if (selected) select(selected);
  }

  function initAll(scope) {
    (scope || document).querySelectorAll(ROOT_SELECTOR).forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }
  document.addEventListener('shopify:section:load', (e) => initAll(e.target));
})();
